import type { Spx } from '@wix/thunderbolt-becky-types'
import { StylePropItem } from '@wix/thunderbolt-becky-root'

// Registry imports
import { initializeConverters, isConvertersRegistryInitialized } from './converters-init'
import { convertersRegistry, getConverter, hasConverter, getRegisteredConverterKeys } from './convertersRegistry'

// Initialize converters registry on module load
initializeConverters()

/**
 * Converts a style property to CSS variables using the registry-based system.
 */
export const convertStyleProp = (styleProp: StylePropItem, propName: string, spx: Spx): Record<string, string> => {
	const converter = getConverter(styleProp.type)

	if (!converter) {
		console.warn(
			`No converter found in registry for style property type: ${
				styleProp.type
			}. Available converters: ${getRegisteredConverterKeys().join(', ')}`
		)
		return {}
	}

	const cssProps = converter(styleProp, spx)

	if (typeof cssProps === 'string') {
		return { [`--${propName}`]: cssProps }
	}

	const cssVars: Record<string, string> = {}
	Object.entries(cssProps).forEach(([key, subpropValue]) => {
		if (subpropValue) {
			if (key === styleProp.type) {
				// @ts-ignore
				cssVars[`--${propName}`] = subpropValue.replaceAll(`{propName}`, propName)
			} else {
				cssVars[`--${propName}-${key}`] = subpropValue
			}
		}
	})

	return cssVars
}

// Registry-based API exports
export {
	// Registry access
	convertersRegistry,
	getConverter,
	hasConverter,
	getRegisteredConverterKeys,
	// Initialization functions
	initializeConverters,
	isConvertersRegistryInitialized,
}

/**
 * Utility function to validate registry completeness during development
 */
export const validateRegistryCompleteness = (): {
	isComplete: boolean
	registeredCount: number
	registeredConverters: Array<string>
} => {
	const registeredConverters = getRegisteredConverterKeys()
	const registeredCount = registeredConverters.length

	return {
		isComplete: registeredCount > 0,
		registeredCount,
		registeredConverters: registeredConverters.sort(),
	}
}

/**
 * Example usage with registry:
 *
 * ```typescript
 * import { getConverter, hasConverter, convertStyleProp, validateRegistryCompleteness } from './convertStyleProps'
 *
 * // Check registry status
 * const status = validateRegistryCompleteness()
 * console.log(`Registry has ${status.registeredCount} converters:`, status.registeredConverters)
 *
 * // Check if a specific converter exists
 * if (hasConverter('Color')) {
 *   const colorConverter = getConverter('Color')
 *   const cssValue = colorConverter?.(colorStyleProp)
 * }
 *
 * // Convert style property to CSS variables
 * const cssVars = convertStyleProp(styleProp, 'myProp', spx)
 *
 * // Get all available converters
 * const allConverters = getRegisteredConverterKeys()
 * ```
 */
